import { useState } from "react";
import { Check, MessageSquare } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import SEO from "../components/SEO";
import { ContactHours } from "../components/AboutTeam";
import { apiClient, formatApiError } from "../lib/api";
import { toast, Toaster } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/contact", form);
      setSent(true);
      toast.success("Message envoyé !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <SEO
        title="Contact"
        description="Une question sur votre projet locatif Airbnb autour de Saint-Malo ? Contactez Bretagne Conciergerie Comparateur via le formulaire."
      />
      <Toaster position="top-right" richColors />
      <section className="pt-12 pb-8 md:pt-20 md:pb-12">
        <div className="container-tm max-w-3xl">
          <span className="pill">Contact</span>
          <h1 className="h-display mt-5">
            Une question sur votre<br /><span className="text-accent">projet locatif</span> ?
          </h1>
          <p className="mt-5 text-muted-foreground text-lg">
            Envoyez-nous un message via ce formulaire. Pour trouver une conciergerie, faites directement votre <a href="/devis" className="text-accent underline">demande de devis gratuite</a>.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="container-tm max-w-2xl">
          <div className="mb-6"><ContactHours /></div>
          {sent ? (
            <div className="surface-card p-12 text-center" data-testid="contact-success">
              <div className="inline-flex w-16 h-16 rounded-full bg-accent/10 text-accent items-center justify-center">
                <Check size={32} strokeWidth={2.5} />
              </div>
              <h2 className="h-card text-2xl md:text-3xl mt-4">Message envoyé</h2>
              <p className="mt-3 text-muted-foreground">Nous vous répondons 7j/7 entre 10h et 20h, et vous rappelons par téléphone si besoin.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="surface-card p-7 md:p-10 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom complet" testid="contact-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <Field label="Email" type="email" testid="contact-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <Field label="Téléphone" testid="contact-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                <Field label="Sujet" testid="contact-subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} required />
              </div>
              <div>
                <label className="text-sm font-semibold">Message *</label>
                <textarea
                  rows={6}
                  required
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="contact-message"
                  className="mt-3 w-full px-5 py-4 border border-input rounded-2xl bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-accent w-full disabled:opacity-50" data-testid="contact-submit">
                <MessageSquare size={14} /> {loading ? "Envoi…" : "Envoyer mon message"}
              </button>
            <p className="text-xs text-muted-foreground text-center">Vos données sont utilisées uniquement pour répondre à votre demande. <a href="/politique-de-confidentialite" className="underline">Politique de confidentialité</a></p>
</form>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}{required && " *"}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-2 w-full px-5 py-3.5 border border-input rounded-full bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
      />
    </label>
  );
}
